import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { api } from "@/lib/api";

const THEME = {
  background: "#000000",
  foreground: "#e4e4e7",
  cursor: "#3b82f6",
  selectionBackground: "#334155",
  black: "#000000",
  red: "#ef4444",
  green: "#22c55e",
  yellow: "#eab308",
  blue: "#3b82f6",
  magenta: "#a855f7",
  cyan: "#06b6d4",
  white: "#e4e4e7",
  brightGreen: "#4ade80",
  brightBlue: "#60a5fa",
};

const Terminal = forwardRef(function Terminal(
  { sessionId, initialCwd = "/", fontSize = 14, onCwdChange, onCommandDone, enforceManual = false, onPasteBlocked },
  ref
) {
  const containerRef = useRef(null);
  const term = useRef(null);
  const fit = useRef(null);
  const cwd = useRef(initialCwd);
  const line = useRef("");
  const cursorPos = useRef(0);
  const busy = useRef(false);
  const completing = useRef(false);
  const healedShown = useRef(false);
  const history = useRef([]);
  const histIdx = useRef(-1);
  const rsearch = useRef({ active: false, query: "", idx: -1, match: "" });
  const enforce = useRef(enforceManual);
  const lastBlockLog = useRef(0);
  enforce.current = enforceManual;

  const prompt = () => {
    const t = term.current;
    const path = cwd.current === "/" ? "~" : "~" + cwd.current;
    t.write(`\r\n\x1b[1;32mstudent@lab\x1b[0m:\x1b[1;34m${path}\x1b[0m$ `);
  };

  const redrawLine = () => {
    const t = term.current;
    const path = cwd.current === "/" ? "~" : "~" + cwd.current;
    t.write("\r\x1b[K");
    t.write(`\x1b[1;32mstudent@lab\x1b[0m:\x1b[1;34m${path}\x1b[0m$ ${line.current}`);
  };

  // ---- Ctrl+R reverse history search ----
  const findMatch = (query, fromIdx) => {
    const h = history.current;
    let start = fromIdx == null ? h.length - 1 : fromIdx;
    if (start > h.length - 1) start = h.length - 1;
    for (let i = start; i >= 0; i--) {
      if (!query || h[i].includes(query)) return i;
    }
    return -1;
  };

  const renderSearch = () => {
    const t = term.current;
    const rs = rsearch.current;
    t.write("\r\x1b[K");
    t.write(`\x1b[33m(reverse-i-search)\x1b[0m\`${rs.query}': ${rs.match || ""}`);
  };

  const research = () => {
    const idx = findMatch(rsearch.current.query, history.current.length - 1);
    rsearch.current.idx = idx;
    rsearch.current.match = idx >= 0 ? history.current[idx] : "";
    renderSearch();
  };

  const handleCtrlR = () => {
    const rs = rsearch.current;
    if (!rs.active) {
      rsearch.current = { active: true, query: "", idx: history.current.length, match: "" };
      research();
    } else {
      const idx = findMatch(rs.query, rs.idx - 1);
      if (idx >= 0) { rs.idx = idx; rs.match = history.current[idx]; renderSearch(); }
      else term.current.write("\x07");
    }
  };

  const exitSearch = (keepOnLine) => {
    const rs = rsearch.current;
    const match = rs.match;
    rs.active = false; rs.query = ""; rs.match = ""; rs.idx = -1;
    if (keepOnLine && match) {
      line.current = match;
      cursorPos.current = match.length;
      redrawLine();
    }
    return match;
  };

  const handleSearchKey = (data, code) => {
    const t = term.current;
    const rs = rsearch.current;
    if (data === "\r") {
      const m = rs.match || "";
      rs.active = false; rs.query = ""; rs.match = ""; rs.idx = -1;
      line.current = m; cursorPos.current = m.length;
      redrawLine();
      if (m.trim()) {
        const cmd = m.trim();
        history.current.push(cmd);
        histIdx.current = history.current.length;
        line.current = ""; cursorPos.current = 0;
        runCommand(cmd);
      } else { prompt(); }
      return;
    }
    if (data === "\x03") { // Ctrl+C — abort search
      rs.active = false; rs.query = ""; rs.match = ""; rs.idx = -1;
      line.current = ""; cursorPos.current = 0;
      t.write("^C"); prompt();
      return;
    }
    if (data === "\x1b" || data === "\x07") { exitSearch(true); return; } // ESC / Ctrl+G
    if (data.length > 1 && data.charCodeAt(0) === 27) { exitSearch(true); return; } // arrows etc.
    if (code === 127) { // backspace edits the query
      rs.query = rs.query.slice(0, -1);
      research();
      return;
    }
    if (code >= 32 && data.length === 1) {
      rs.query += data;
      research();
      return;
    }
  };

  const runCommand = async (cmd) => {
    const t = term.current;
    busy.current = true;
    if (cmd === "clear") {
      t.clear();
      busy.current = false;
      prompt();
      return;
    }
    try {
      const { data } = await api.post(`/sessions/${sessionId}/exec`, { command: cmd });
      if (data.healed && !healedShown.current) {
        healedShown.current = true;
        t.write("\r\n\x1b[90m↻ Reconnected to your lab session — your previous work has been restored.\x1b[0m");
      }
      if (data.output) t.write(data.output.startsWith("\r\n") ? data.output : "\r\n" + data.output);
      if (data.cwd !== cwd.current) {
        cwd.current = data.cwd;
        onCwdChange && onCwdChange(data.cwd);
      }
      onCommandDone && onCommandDone();
    } catch (e) {
      t.write("\r\n\x1b[31mError: could not reach lab environment.\x1b[0m");
    } finally {
      busy.current = false;
      prompt();
    }
  };

  const commonPrefix = (arr) => {
    if (!arr.length) return "";
    let p = arr[0];
    for (const s of arr) {
      while (!s.startsWith(p)) p = p.slice(0, -1);
      if (!p) break;
    }
    return p;
  };

  const doComplete = async () => {
    if (completing.current || busy.current) return;
    completing.current = true;
    const t = term.current;
    try {
      const { data } = await api.post(`/sessions/${sessionId}/complete`, { line: line.current, cwd: cwd.current });
      const comps = data.completions || [];
      if (comps.length === 0) { t.write("\x07"); return; } // bell
      const lastSpace = line.current.lastIndexOf(" ");
      const base = lastSpace >= 0 ? line.current.slice(0, lastSpace + 1) : "";
      const token = data.prefix ?? line.current.slice(base.length);
      if (comps.length === 1) {
        let newLine = base + comps[0];
        if (!comps[0].endsWith("/")) newLine += " ";
        line.current = newLine;
        cursorPos.current = newLine.length;
        redrawLine();
      } else {
        const lcp = commonPrefix(comps);
        if (lcp && lcp.length > token.length) {
          const newLine = base + lcp;
          line.current = newLine;
          cursorPos.current = newLine.length;
          redrawLine();
        } else {
          t.write("\r\n\x1b[90m" + comps.join("   ") + "\x1b[0m");
          prompt();
          t.write(line.current);
        }
      }
    } catch { /* ignore completion errors */ }
    finally { completing.current = false; }
  };

  const blockPaste = (method) => {
    const t = term.current;
    t.write("\r\n\x1b[31mCopy and paste is disabled in this hands-on lab. Please type the command manually to continue.\x1b[0m\r\n");
    redrawLine();
    const now = Date.now();
    if (onPasteBlocked && now - lastBlockLog.current > 3000) {
      lastBlockLog.current = now;
      onPasteBlocked(method);
    }
  };

  const onKey = (data) => {
    if (busy.current) return;
    const t = term.current;
    const code = data.charCodeAt(0);

    // Paste / injection detection: a real keystroke is a single char (or an escape
    // sequence starting with ESC 0x1b). Any other multi-char burst is pasted/injected text.
    if (enforce.current && data.length > 1 && data.charCodeAt(0) !== 27) {
      blockPaste("bulk-input");
      return;
    }

    if (data === "\x12") { // Ctrl+R — reverse history search
      handleCtrlR();
      return;
    }
    if (rsearch.current.active) {
      handleSearchKey(data, code);
      return;
    }

    if (data === "\r") {
      const cmd = line.current.trim();
      if (cmd) {
        history.current.push(cmd);
        histIdx.current = history.current.length;
        line.current = "";
        cursorPos.current = 0;
        runCommand(cmd);
      } else {
        prompt();
      }
      return;
    }
    if (code === 127) {
      // backspace
      if (line.current.length > 0) {
        line.current = line.current.slice(0, -1);
        cursorPos.current = line.current.length;
        t.write("\b \b");
      }
      return;
    }
    if (data === "\x03") {
      // Ctrl+C
      t.write("^C");
      line.current = "";
      cursorPos.current = 0;
      prompt();
      return;
    }
    if (data === "\x1b[A") {
      // up
      if (history.current.length && histIdx.current > 0) {
        histIdx.current -= 1;
        line.current = history.current[histIdx.current];
        cursorPos.current = line.current.length;
        redrawLine();
      }
      return;
    }
    if (data === "\x1b[B") {
      // down
      if (histIdx.current < history.current.length - 1) {
        histIdx.current += 1;
        line.current = history.current[histIdx.current];
      } else {
        histIdx.current = history.current.length;
        line.current = "";
      }
      cursorPos.current = line.current.length;
      redrawLine();
      return;
    }
    if (data === "\t") {
      // Tab autocomplete (commands in command position, files/dirs otherwise)
      doComplete();
      return;
    }
    if (code < 32) return; // ignore other control chars
    line.current += data;
    cursorPos.current = line.current.length;
    t.write(data);
  };

  useImperativeHandle(ref, () => ({
    focus: () => term.current?.focus(),
    clear: () => {
      term.current?.clear();
      prompt();
    },
    reset: () => {
      const t = term.current;
      t.clear();
      cwd.current = "/";
      line.current = "";
      onCwdChange && onCwdChange("/");
      t.write("\x1b[33mLab environment reset to its initial state.\x1b[0m");
      prompt();
    },
    setFontSize: (fs) => {
      if (term.current) {
        term.current.options.fontSize = fs;
        setTimeout(() => fit.current?.fit(), 30);
      }
    },
    refit: () => setTimeout(() => fit.current?.fit(), 30),
  }));

  useEffect(() => {
    const t = new XTerm({
      fontSize,
      fontFamily: '"JetBrains Mono", monospace',
      theme: THEME,
      cursorBlink: true,
      convertEol: false,
      scrollback: 2000,
    });
    const f = new FitAddon();
    t.loadAddon(f);
    t.open(containerRef.current);
    setTimeout(() => f.fit(), 30);
    t.writeln("\x1b[1;34mHMG Linux Lab\x1b[0m — live Linux lab shell (real, isolated bash sandbox).");
    t.writeln("\x1b[90mType commands below. Try: pwd, ls, echo hello. Restricted: sudo, network, system dirs.\x1b[0m");
    term.current = t;
    fit.current = f;
    prompt();
    t.focus();
    const disp = t.onData(onKey);

    // Defense-in-depth: block paste at the browser/DOM layer too (not just Ctrl+V).
    t.attachCustomKeyEventHandler((e) => {
      if (e.type === "keydown") {
        const key = (e.key || "").toLowerCase();
        // Ctrl+R = reverse-search inside the terminal — stop the browser page reload,
        // but still let xterm emit the keystroke so onKey() handles it.
        if ((e.ctrlKey || e.metaKey) && key === "r" && !e.shiftKey) { e.preventDefault(); return true; }
      }
      if (!enforce.current || e.type !== "keydown") return true;
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "v") { e.preventDefault(); blockPaste("ctrl-v"); return false; }
      if (e.shiftKey && (e.key === "Insert")) { e.preventDefault(); blockPaste("shift-insert"); return false; }
      return true;
    });

    const el = containerRef.current;
    const stop = (method) => (ev) => {
      if (!enforce.current) return;
      ev.preventDefault();
      ev.stopPropagation();
      blockPaste(method);
    };
    const onPaste = stop("paste-event");
    const onCtx = stop("context-menu");
    const onDrop = stop("drag-drop");
    const onDragOver = (ev) => { if (enforce.current) ev.preventDefault(); };
    const onAux = (ev) => { if (enforce.current && ev.button === 1) { ev.preventDefault(); blockPaste("middle-click"); } };
    el.addEventListener("paste", onPaste, true);
    el.addEventListener("contextmenu", onCtx, true);
    el.addEventListener("drop", onDrop, true);
    el.addEventListener("dragover", onDragOver, true);
    el.addEventListener("auxclick", onAux, true);
    el.addEventListener("mousedown", onAux, true);

    const onResize = () => setTimeout(() => f.fit(), 30);
    window.addEventListener("resize", onResize);
    return () => {
      disp.dispose();
      window.removeEventListener("resize", onResize);
      el.removeEventListener("paste", onPaste, true);
      el.removeEventListener("contextmenu", onCtx, true);
      el.removeEventListener("drop", onDrop, true);
      el.removeEventListener("dragover", onDragOver, true);
      el.removeEventListener("auxclick", onAux, true);
      el.removeEventListener("mousedown", onAux, true);
      t.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return <div ref={containerRef} className="h-full w-full terminal-shell" data-testid="lab-terminal" />;
});

export default Terminal;
